"use client";

import { Form, Formik, type FormikHelpers, useField, useFormikContext } from "formik";
import { type ChangeEvent, useState } from "react";

import kebabToCamelCase from "@shared/functions/kebabToCamelCase";

import postCCPAForm from "@server/front-end-api/postCCPAForm";

import ValidationError from "@client/components/ccpa-form/components/validation-error";
import Checkbox from "@client/components/formik/checkbox";
import Field from "@client/components/formik/field";
import Input from "@client/components/formik/input";
import PhoneNumber from "@client/components/formik/phone-number";
import Select from "@client/components/formik/select";
import ZipCode from "@client/components/formik/zip-code";
import { postEvent } from "@client/functions/client-actions";

import { FormButton } from "@ui/js/components/buttons/form-button";

import type TCCPAForm from "@packages/types/ccpa-form";
import {
  CCPA_STATES_WITH_PRIVACY_LAWS,
  initialValues,
  validationSchema,
} from "@packages/types/yup/ccpa-form";

// Input IDs and Labels constants
const INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE = "ccpa-requestor-type";

const INPUT_ID_AND_LABEL_HTMLFOR_AGENT_FIRST_NAME =
  "authorized-agent-first-name";
const INPUT_ID_AND_LABEL_HTMLFOR_AGENT_LAST_NAME = "authorized-agent-last-name";
const INPUT_ID_AND_LABEL_HTMLFOR_AGENT_EMAIL = "authorized-agent-email";
const INPUT_ID_AND_LABEL_HTMLFOR_AGENT_PHONE_NUMBER =
  "authorized-agent-phone-number";

const INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME = "first-name";
const INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME = "last-name";
const INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS = "street-address";
const INPUT_ID_AND_LABEL_HTMLFOR_CITY = "city";
const INPUT_ID_AND_LABEL_HTMLFOR_STATE = "state";
const INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE = "zip-code";
const INPUT_ID_AND_LABEL_HTMLFOR_EMAIL = "email";
const INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER = "phone-number";

const CCPA_REQUEST_TYPES_FIELD_NAME = "ccpaRequestTypes";

interface CustomDocument extends Document {
  ccpa: HTMLFormElement;
}

function SiteNameField() {
  const [field] = useField<string>("siteName");
  return (
    <input type="hidden" name={field.name} value={field.value} readOnly={true} />
  );
}

function RequestorTypeField() {
  const { setFieldValue } = useFormikContext<TCCPAForm.Values>();
  const fieldName = kebabToCamelCase(
    INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE,
  );

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;

    setFieldValue(fieldName, value);

    if (value !== "Authorized Agent") {
      setFieldValue(
        kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_FIRST_NAME),
        "",
      );
      setFieldValue(
        kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_LAST_NAME),
        "",
      );
      setFieldValue(
        kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_EMAIL),
        "",
      );
      setFieldValue(
        kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_PHONE_NUMBER),
        "",
      );
    }
  };

  return (
    <Field
      id={INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE}
      name={fieldName}
      label="Requestor"
    >
      <Select
        required={true}
        id={INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE}
        name={fieldName}
        onChange={handleChange}
      >
        <option value="Self">Self</option>
        <option value="Authorized Agent">Authorized Agent</option>
      </Select>
    </Field>
  );
}

function StateField() {
  const { setFieldValue } = useFormikContext<TCCPAForm.Values>();

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;

    setFieldValue(kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STATE), value)
    setFieldValue(CCPA_REQUEST_TYPES_FIELD_NAME, []);
  };

  return (
    <>
      <Field
        id={INPUT_ID_AND_LABEL_HTMLFOR_STATE}
        name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STATE)}
        label="State"
      >
        <Select
          id={INPUT_ID_AND_LABEL_HTMLFOR_STATE}
          name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STATE)}
          onChange={handleChange}
        >
          <option value="" disabled={true}>
            Select a state
          </option>
          <option value="AL">Alabama</option>
          <option value="AK">Alaska</option>
          <option value="AZ">Arizona</option>
          <option value="AR">Arkansas</option>
          <option value="CA">California</option>
          <option value="CO">Colorado</option>
          <option value="CT">Connecticut</option>
          <option value="DE">Delaware</option>
          <option value="DC">District Of Columbia</option>
          <option value="FL">Florida</option>
          <option value="GA">Georgia</option>
          <option value="HI">Hawaii</option>
          <option value="ID">Idaho</option>
          <option value="IL">Illinois</option>
          <option value="IN">Indiana</option>
          <option value="IA">Iowa</option>
          <option value="KS">Kansas</option>
          <option value="KY">Kentucky</option>
          <option value="LA">Louisiana</option>
          <option value="ME">Maine</option>
          <option value="MD">Maryland</option>
          <option value="MA">Massachusetts</option>
          <option value="MI">Michigan</option>
          <option value="MN">Minnesota</option>
          <option value="MS">Mississippi</option>
          <option value="MO">Missouri</option>
          <option value="MT">Montana</option>
          <option value="NE">Nebraska</option>
          <option value="NV">Nevada</option>
          <option value="NH">New Hampshire</option>
          <option value="NJ">New Jersey</option>
          <option value="NM">New Mexico</option>
          <option value="NY">New York</option>
          <option value="NC">North Carolina</option>
          <option value="ND">North Dakota</option>
          <option value="OH">Ohio</option>
          <option value="OK">Oklahoma</option>
          <option value="OR">Oregon</option>
          <option value="PA">Pennsylvania</option>
          <option value="RI">Rhode Island</option>
          <option value="SC">South Carolina</option>
          <option value="SD">South Dakota</option>
          <option value="TN">Tennessee</option>
          <option value="TX">Texas</option>
          <option value="UT">Utah</option>
          <option value="VT">Vermont</option>
          <option value="VA">Virginia</option>
          <option value="WA">Washington</option>
          <option value="WV">West Virginia</option>
          <option value="WI">Wisconsin</option>
          <option value="WY">Wyoming</option>
        </Select>
      </Field>
    </>
  )
}

function HiddenRequestTypes() {
  const [field] = useField<string[]>(CCPA_REQUEST_TYPES_FIELD_NAME);
  return (
    <input
      type="hidden"
      name={CCPA_REQUEST_TYPES_FIELD_NAME}
      value={field.value.join(",")}
    />
  );
}

function RequestTypeFields() {
  const fieldName = CCPA_REQUEST_TYPES_FIELD_NAME;
  const [state] = useField<string>(INPUT_ID_AND_LABEL_HTMLFOR_STATE);

  if (!CCPA_STATES_WITH_PRIVACY_LAWS.includes(state.value)) {
    return (<HiddenRequestTypes />);
  } else {
    return (
      <>
        <Field
          name={fieldName}
          label={"I would like to submit the following requests:"}
        >
          <HiddenRequestTypes />
          <Checkbox
            id="ccpa-request-types-1"
            label="Request to know how my data is collected and processed"
            name={fieldName}
            required={false}
            value="Request to know how my data is collected and processed"
          />
          <Checkbox
            id="ccpa-request-types-2"
            label="Request to access a portable version of my data"
            name={fieldName}
            required={false}
            value="Request to access a portable version of my data"
          />
          <Checkbox
            id="ccpa-request-types-3"
            label="Request to correct my data"
            name={fieldName}
            required={false}
            value="Request to correct my data"
          />
          <Checkbox
            id="ccpa-request-types-4"
            label="Request to delete my data"
            name={fieldName}
            required={false}
            value="Request to delete my data"
          />
          <Checkbox
            id="ccpa-request-types-5"
            label="Request to not sell my data"
            name={fieldName}
            required={false}
            value="Request to not sell my data"
          />
          <Checkbox
            id="ccpa-request-types-6"
            label="Request to not share my data"
            name={fieldName}
            required={false}
            value="Request to not share my data"
          />
          <Checkbox
            id="ccpa-request-types-7"
            label="Request to revoke consents regarding my data"
            name={fieldName}
            required={false}
            value="Request to revoke consents regarding my data"
          />
        </Field>
      </>
    )
  }
}

function AuthorizedAgentFields() {
  const { values } = useFormikContext<TCCPAForm.Values>();

  if (values.ccpaRequestorType !== "Authorized Agent") {
    return null;
  }

  return (
    <>
      <Field
        id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_FIRST_NAME}
        name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_FIRST_NAME)}
        label="Authorized Agent First name"
      >
        <Input
          id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_FIRST_NAME}
          name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_FIRST_NAME)}
        />
      </Field>
      <Field
        id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_LAST_NAME}
        name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_LAST_NAME)}
        label="Authorized Agent Last name"
      >
        <Input
          id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_LAST_NAME}
          name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_LAST_NAME)}
        />
      </Field>
      <Field
        id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_EMAIL}
        name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_EMAIL)}
        label="Authorized Agent Email address"
      >
        <Input
          id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_EMAIL}
          type="email"
          name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_EMAIL)}
          placeholder="name@example.com"
        />
      </Field>
      <Field
        id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_PHONE_NUMBER}
        name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_PHONE_NUMBER)}
        label="Authorized Agent Phone"
      >
        <PhoneNumber
          id={INPUT_ID_AND_LABEL_HTMLFOR_AGENT_PHONE_NUMBER}
          name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_AGENT_PHONE_NUMBER)}
        />
      </Field>
    </>
  );
}

export default function CCPAForm() {
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (
    values: TCCPAForm.Values,
    formikHelpers: FormikHelpers<TCCPAForm.Values>,
  ) => {
    const { error } = await postCCPAForm(values);

    if (error) {
      postEvent("error", {
        error: {
          error_message: `An error occurred while attempting to submit the CCPA form.`,
          error_object: "CCPA Form",
          error_type: "Front-End API",
          path: window.location.href,
        },
      });

      formikHelpers.setStatus("error");
      return;
    }

    setSuccess(true);

    formikHelpers.resetForm();

    (document as CustomDocument).ccpa
      .querySelectorAll('input[type="checkbox"]:checked')
      .forEach(($input) => {
        $input.parentElement?.click();
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      <Form
        className="form"
        name="ccpa"
        noValidate={true}
        data-spec-ccpa-form
        method="POST"
      >
        <SiteNameField />
        <RequestorTypeField />
        <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
          <AuthorizedAgentFields />
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME)}
            label="First name"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME)}
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME)}
            label="Last name"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME)}
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS)}
            label="Street Address"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS)}
              placeholder="3170 N Sheridan"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_CITY}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_CITY)}
            label="City"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_CITY}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_CITY)}
              placeholder="Chicago"
            />
          </Field>
          <StateField />
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE)}
            label="Zip"
          >
            <ZipCode
              id={INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE)}
              placeholder="60657"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_EMAIL)}
            label="Email address"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
              type="email"
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_EMAIL)}
              placeholder="name@example.com"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER)}
            label="Phone"
          >
            <PhoneNumber
              id={INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER)}
            />
          </Field>
        </div>
        <RequestTypeFields />
        <FormButton text="Submit" />
        <ValidationError />
        {success && (
          <div
            className="bg-green-50 rounded-lg p-4 text-green-900"
            data-spec="ccpa-form-success"
          >
            We have received your request.
          </div>
        )}
      </Form>
    </Formik>
  );
}
